import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PackageOpen, Wallet, MessageSquare, AlertTriangle, Search, Loader2, Star, History , Printer } from "lucide-react";
import api from "../console/api";

const EDGE = "#E8DFCB", PLUM = "#3E1F46", OLIVE = "#5A6B3E";
export const LAST_LIST_KEY = "heritage-admin-last-list";
const FAV_KEY = "heritage-admin-favourites";
const ICONS = { pack: PackageOpen, pay: Wallet, reply: MessageSquare };

/** "What needs me today?" , three real actions, a decision list, search, favourites and "continue where I left off". */
export default function AdminToday() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [favs, setFavs] = useState(() => { try { return JSON.parse(localStorage.getItem(FAV_KEY) || "[]"); } catch { return []; } });
  const last = (() => { try { return JSON.parse(localStorage.getItem(LAST_LIST_KEY) || "null"); } catch { return null; } })();

  useEffect(() => {
    api.get("/admin/today").then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Couldn't load today's counts , showing nothing rather than a misleading zero."));
  }, []);

  const search = (e) => {
    e.preventDefault();
    const t = q.trim();
    if (!t) return;
    if (/^(HD-|#)?\d{3,}$|^[0-9a-f]{8}$/i.test(t)) navigate(`/admin/orders/all?q=${encodeURIComponent(t.replace(/^#/, ""))}`);
    else navigate(`/admin/customers?q=${encodeURIComponent(t)}`);
  };
  const toggleFav = (item) => {
    const next = favs.some((f) => f.to === item.to) ? favs.filter((f) => f.to !== item.to) : [...favs, item].slice(0, 8);
    setFavs(next); localStorage.setItem(FAV_KEY, JSON.stringify(next));
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-6xl" data-testid="admin-today">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.35em] uppercase font-semibold" style={{ color: OLIVE }}>✿ Heritage · Today</div>
          <h1 className="font-serif text-4xl sm:text-5xl" style={{ color: "#3C2A1E" }}>What needs me today?</h1>
        </div>
        <form onSubmit={search} className="relative w-full sm:w-96" data-testid="today-search-form">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9C8B73]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="today-search" placeholder="Find a customer, order number, variety…" className="w-full h-11 pl-9 pr-3 rounded-full border bg-white text-sm" style={{ borderColor: PLUM }} />
        </form>
      </header>

      {err && <div className="border rounded p-3 text-sm text-[#A33030] bg-[#FFF4F0]" data-testid="today-error">{err}</div>}
      {!data && !err && <div className="text-sm text-[#7A6A55] flex items-center gap-2" data-testid="today-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading live counts…</div>}

      {data && (
        <>
          <section className="grid gap-3 sm:grid-cols-3" data-testid="today-actions">
            {data.actions.map((a) => { const I = ICONS[a.key] || PackageOpen; return (
              <div key={a.key} className="relative bg-white border-2 rounded-md p-4 flex flex-col gap-2" style={{ borderColor: a.count ? PLUM : EDGE }} data-testid={`today-action-${a.key}`}>
                <button type="button" onClick={() => toggleFav({ label: a.verb, to: a.to })} aria-label="Favourite" className="absolute top-2 right-2 p-1" data-testid={`today-fav-${a.key}`}>
                  <Star className="w-4 h-4" style={{ color: favs.some((f) => f.to === a.to) ? "#C9A227" : "#CFC5B2" }} fill={favs.some((f) => f.to === a.to) ? "#C9A227" : "none"} />
                </button>
                <div className="flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase font-semibold" style={{ color: OLIVE }}><I className="w-4 h-4" /> {a.key}</div>
                <div className="font-serif text-4xl" style={{ color: PLUM }} data-testid={`today-count-${a.key}`}>{a.count}</div>
                <div className="text-xs text-[#7A6A55]">{a.sub}</div>
                <Link to={a.to} data-testid={`today-open-${a.key}`} className="mt-auto inline-flex justify-center h-11 items-center rounded text-white text-[12px] tracking-[0.2em] uppercase font-semibold" style={{ backgroundColor: a.count ? PLUM : "#9C8B73" }}>{a.verb}</Link>
              </div>
            ); })}
          </section>

          <section className="grid gap-4 lg:grid-cols-[1.3fr_1fr]">
            <div className="bg-[#FBF6E9] border rounded-md p-4" style={{ borderColor: EDGE }} data-testid="today-decisions">
              <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-semibold" style={{ color: PLUM }}><AlertTriangle className="w-4 h-4" /> Needs a decision</div>
              {data.decisions.length === 0 ? <p className="text-sm text-[#7A6A55] mt-2" data-testid="today-decisions-empty">Nothing waiting on you right now.</p> : (
                <ul className="mt-2 divide-y" style={{ borderColor: EDGE }}>
                  {data.decisions.map((d, i) => (
                    <li key={i} className="py-2 flex items-start gap-3" data-testid={`today-decision-${d.kind}-${i}`}>
                      <span className={"mt-1 text-[9px] tracking-[0.2em] uppercase font-semibold px-1.5 py-0.5 rounded " + (d.urgent ? "bg-[#A33030] text-white" : "bg-[#E8DFCB] text-[#3C2A1E]")}>{d.urgent ? "Urgent" : "Incomplete"}</span>
                      <div className="flex-1 min-w-0"><div className="text-sm text-[#3C2A1E]">{d.label}</div>{d.detail && <div className="text-xs text-[#7A6A55] truncate">{d.detail}</div>}</div>
                      <Link to={d.to} className="text-xs underline" style={{ color: PLUM }}>Open</Link>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-[11px] text-[#9C8B73] mt-3">Can wait: {data.can_wait.on_the_way} on the way · {data.can_wait.held} on hold. {data.scope}</p>
            </div>
            <div className="space-y-3">
              <div className="bg-white border rounded-md p-4" style={{ borderColor: EDGE }} data-testid="today-continue">
                <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-semibold" style={{ color: PLUM }}><History className="w-4 h-4" /> Where was I?</div>
                {last ? <Link to={last.to} className="mt-2 inline-flex h-11 items-center px-4 rounded border text-sm" style={{ borderColor: PLUM, color: PLUM }} data-testid="today-continue-link">Continue: {last.label}</Link>
                  : <p className="text-sm text-[#7A6A55] mt-2">Open any list and it will appear here with its filters.</p>}
              </div>
              <div className="bg-white border rounded-md p-4" style={{ borderColor: EDGE }} data-testid="today-print">
                <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-semibold" style={{ color: PLUM }}><Printer className="w-4 h-4" /> Documents & Print</div>
                <p className="text-sm text-[#7A6A55] mt-1">Invoices, packing slips, checklists, pull list, labels, inserts and seals , preview, print or download.</p>
                <Link to="/admin/print" data-testid="today-print-link" className="mt-2 inline-flex h-11 items-center px-4 rounded border text-sm" style={{ borderColor: PLUM, color: PLUM }}>Open Documents & Print</Link>
              </div>
              <div className="bg-white border rounded-md p-4" style={{ borderColor: EDGE }} data-testid="today-favourites">
                <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-semibold" style={{ color: PLUM }}><Star className="w-4 h-4" /> Favourites</div>
                {favs.length === 0 ? <p className="text-sm text-[#7A6A55] mt-2">Star an action to pin it here.</p> :
                  <div className="flex flex-wrap gap-2 mt-2">{favs.map((f) => <Link key={f.to} to={f.to} className="h-9 inline-flex items-center px-3 rounded-full border text-xs" style={{ borderColor: OLIVE, color: OLIVE }}>{f.label}</Link>)}</div>}
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
